/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flatten from 'lodash.flatten';
import { autorun } from 'mobx';
import * as React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useModels } from 'src/luis/modules/models/useModels';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { PrebuiltEntityMetadata } from 'src/luis/projects/entities/types/PrebuiltEntityMetadata';
import { entitiesDenormalizer } from 'src/luis/projects/entities/utils/entitiesDenormalizer';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';

/**
 * A hook used to initialize the app entities denormalized by doing the following:
 *
 * 1) Fetches the current app entities, intents and phrase lists.
 * 2) Denormalizes entity stores into entity objects.
 *
 * The entities wrapped in autorun hook to observe the changes and recompute the objects only when
 * something changes in the stores.
 */
export const useEntities = () => {
    const { rootStore } = useGlobalState();
    const { entityStores, intentStores, phraseListStores, isLoaded, isLoading } = useModels();
    const [entities, setEntities] = React.useState<Entity[]>(() =>
        isLoaded ? entitiesDenormalizer(entityStores, intentStores, phraseListStores) : []
    );

    React.useEffect(
        () =>
            autorun(() => {
                setEntities(isLoaded ? entitiesDenormalizer(entityStores, intentStores, phraseListStores) : []);
            }),
        [isLoaded, entityStores, intentStores, phraseListStores]
    );

    const { items: domains } = rootStore.currentLuisAppStore.domainStoreHouse;
    const { items: rawPrebuiltEntities } = rootStore.currentLuisAppStore.prebuiltEntityStoreHouse;

    const prebuiltDomainEntities: DomainModel[] = React.useMemo(
        () =>
            flatten(
                domains.map(domain =>
                    domain.entities.map(e => ({
                        domainName: domain.name,
                        modelName: e.name,
                        name: `${domain.name}.${e.name}`
                    }))
                )
            ).filter(entity => !entities.find(e => e.name === entity.name)),
        [entities, domains]
    );

    const prebuiltEntities: PrebuiltEntityMetadata[] = React.useMemo(
        () => rawPrebuiltEntities.filter(item => !entities.find(e => e.name === item.name)),
        [entities, rawPrebuiltEntities]
    );

    return { entities, isLoaded, isLoading, prebuiltDomainEntities, prebuiltEntities };
};
